import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import client from "utils/client"

export const useConfirmUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id) => {
      const response = await client.post(`/v2/users/${id}/confirmations.json`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["internal_users"]
      })
      queryClient.invalidateQueries({
        queryKey: ["v2_members", "index"]
      })
    }
  })
}

export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (values) => {
      let url = "password/edit.json"

      if (values.reset_password_token) {
        url = "/users/password.json"
      } else if (values.confirmation_token) {
        url = "/users/confirmations.json"
      }

      const res = await client.put(url, {
        user: values
      })

      return res.data
    }
  })
}

export const useRequestPasswordChange = () => {
  return useMutation({
    mutationFn: async (values) => {
      const res = await client.post("/users/password.json", {
        user: values
      })
      return res.data
    }
  })
}

export const useLogin = () => {
  return useMutation({
    mutationFn: async ({ email, password, otp_attempt, otp_by_email }) => {
      const response = await client.post(
        "/session_tokens.json",
        {
          email,
          password,
          otp_attempt,
          otp_by_email
        },
        { withCredentials: true }
      )

      return response
    }
  })
}

export const useTwoFactorAuthentication = (options = {}) => {
  return useQuery({
    queryKey: ["v2", "users", "two_factor_authentication", "show"],
    queryFn: async () => {
      const res = await client("/v2/users/two_factor_authentication.json")

      return res.data
    },
    ...options
  })
}

export const useCreate2FA = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (values) => {
      const res = await client.post(
        "/v2/users/two_factor_authentication.json",
        values
      )

      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["v2", "users", "two_factor_authentication", "show"]
      })
    }
  })
}

export const useSend2FAEmail = () => {
  return useMutation({
    mutationFn: async () => {
      const res = await client.post("/v2/users/two_factor_emails.json")

      return res.data
    }
  })
}

export const useSendTwoFactorEmailToExternalUser = () => {
  return useMutation({
    mutationFn: async (values) => {
      const res = await client.post(
        "/v2/external_users/two_factor_emails.json",
        values,
        { withCredentials: true }
      )

      return res.data
    }
  })
}

export const useDisable2FA = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (values) => {
      const response = await client.delete(
        "/v2/users/two_factor_authentication.json",
        { data: values }
      )

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["v2", "users", "two_factor_authentication", "show"]
      })
    }
  })
}

export const useShow2FABackupCode = (userId, options = {}) => {
  return useQuery({
    queryKey: ["v2", "users", "two_factor_backup_codes", "show", userId],
    queryFn: async () => {
      const response = await client(
        `/v2/users/${userId}/two_factor_backup_codes.json`
      )

      return response.data
    },
    cacheTime: 0,
    ...options
  })
}

export const useEnforce2FA = (options = {}) => {
  return useQuery({
    queryKey: ["v2", "accounts", "two_factor_enforce", "show"],
    queryFn: async () => {
      const res = await client("/v2/accounts/two_factor_enforce.json")

      return res.data
    },
    ...options
  })
}

export const useUpdateEnforce2FA = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (values) => {
      const res = await client.put(
        "/v2/accounts/two_factor_enforce.json",
        values
      )

      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["v2", "accounts", "two_factor_enforce", "show"]
      })
    }
  })
}

export const useDisableTwoFactorEmail = (options = {}) => {
  return useQuery({
    queryKey: ["v2", "accounts", "two_factor_disable_email", "show"],
    queryFn: async () => {
      const res = await client("/v2/accounts/two_factor_disable_email.json")

      return res.data
    },
    ...options
  })
}

export const useUpdateDisableTwoFactorEmail = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (values) => {
      const res = await client.put(
        "/v2/accounts/two_factor_disable_email.json",
        values
      )

      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["v2", "accounts", "two_factor_disable_email", "show"]
      })
      queryClient.invalidateQueries({
        queryKey: ["v2", "users", "two_factor_authentication", "show"]
      })
    }
  })
}
